<template>
	<div class="donuts-widget">
		<div v-if="!chartData" class="widget-unavailable">
			<span>{{ $t('dashboard_no_data') }}</span>
		</div>
		<div v-else class="doughnut-item">
			<div class="title">{{ $t(donutKey) }}</div>
			<Chart
				type="doughnut"
				:labels="chartData.labels"
				:datasets="chartData.datasets"
				:options="chartOptions"
				height="85%"
				width="100%"
			/>
		</div>
	</div>
</template>

<script>
import { analyticsChartColors } from '../consts/consts'
import { capitalize } from '../../common/services/utilService'
import Chart from '../../common/components/Chart'
import overviewChartService from '../services/overviewChartService'
import utilService from '../../common/services/utilService'
import scssVars from '@/styles/vars.scss'
import { mapGetters } from 'vuex'

export default {
	name: 'DonutsWidget',
	props: {
		config: { type: Object, required: true },
		widgetData: { type: Object, required: false, default: () => ({}) }
	},
	data() {
		return {
			chartOptions: {
				maintainAspectRatio: false,
				legend: {
					position: 'right',
					labels: { usePointStyle: true, padding: 5, fontSize: 10, fontFamily: 'Assistant' }
				},
				tooltips: {
					callbacks: {
						label: (item, tableData) => {
							let str = ''
							str += tableData.labels[item.index] + ' '
							str += +tableData.datasets[0].data[item.index]
							return str
						}
					},
					bodyFontSize: 12,
					titleFontSize: 12,
					backgroundColor: scssVars.lightGrey,
					titleFontColor: scssVars.blackBlue,
					bodyFontColor: scssVars.blackBlue
				}
			}
		}
	},
	computed: {
		...mapGetters(['analyticsData']),
		donutKey() {
			const donut = this.config.donut
			return ['general', 'cleaning', 'maintenance'].includes(donut) ? donut : 'general'
		},
		chartListData() {
			// Same aggregation as OverviewDonuts (operation sites): percent labels on
			// cleaning/maintenance donuts and a likes-vs-problems summary as "general".
			const analyticsChartData = this.analyticsData && this.analyticsData.charts
			if (!analyticsChartData) return null
			const data = JSON.parse(JSON.stringify(analyticsChartData))
			const chartListData = { general: {}, cleaning: {}, maintenance: {} }
			const signals = { cleaning: 0, maintenance: 0, totalLikes: 0 }
			for (const key in data) {
				if (data[key].data) {
					const sorted = utilService.sortParallelArrays(data[key].data, data[key].labels)
					data[key].data = sorted.map((a) => +a.num.toFixed(0))
					data[key].labels = sorted.map((a) => a.str)
					data[key].data.forEach((a) => (signals[key] = signals[key] + a))
					data[key] = overviewChartService.addPercentToLabels(data[key], signals[key])
					let labels
					if (data[key].localLabels && data[key].localLabels[0]) labels = data[key].localLabels
					else if (data[key].labels[0]) labels = data[key].labels
					else labels = []
					chartListData[key] = {
						labels,
						datasets: [
							{
								data: data[key].data[0] ? data[key].data : [1],
								backgroundColor: data[key].data[0] ? analyticsChartColors[key] : '#E9E9E9'
							}
						]
					}
				} else signals[key] = signals[key] + data[key]
			}

			const totalSignals = Object.values(signals).reduce((acc, item) => item + acc, 0)
			const likesProblemsChart = {
				labels: Object.keys(signals).map((a, index) => {
					let labelStr = ''
					labelStr += Math.floor((Object.values(signals)[index] / totalSignals) * 100) + '% '
					if (a === 'totalLikes') labelStr += this.$t('like')
					else labelStr += capitalize(this.$t(a))
					return labelStr
				}),
				datasets: [
					{
						data: Object.values(signals),
						backgroundColor: analyticsChartColors['likes']
					}
				]
			}
			if (!signals.cleaning && !signals.maintenance && !signals.totalLikes) {
				likesProblemsChart.labels = ['No Data']
				likesProblemsChart.datasets[0].data = [1]
				likesProblemsChart.datasets[0].backgroundColor = ['#E9E9E9']
			}
			chartListData.general = likesProblemsChart
			return chartListData
		},
		chartData() {
			if (!this.chartListData) return null
			const chart = this.chartListData[this.donutKey]
			if (!chart || !chart.labels || !chart.labels.length) return null
			return chart
		}
	},
	components: { Chart }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.donuts-widget {
	height: 100%;
	width: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

.widget-unavailable {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #848593;
	font-size: 0.875rem;
	@include border($grey-border-color);
	border-radius: 10px;
	background: #fafbfc;
}

.doughnut-item {
	@include shadow-border;
	padding: 5px;
	width: 100%;
	height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;

	.title {
		margin-bottom: 10px;
		color: #848593;
		font-size: 1.125rem;
	}

	::v-deep .chart-container {
		flex: 1;
		min-height: 0;
	}
}
</style>
